import { forwardRef, useImperativeHandle, useEffect, useRef } from 'react'
import { Badge, Form } from 'antd'
import { DownOutlined, RightOutlined, CloseOutlined, CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrafficCone } from '@fortawesome/pro-solid-svg-icons'
import dayjs, { Dayjs } from 'dayjs'
import { Duration } from '../../common/durationModel'
import { ScheduledEvent, SchedulingJob } from '../scheduleApi'
import { useEventFormState } from '../EventForm/useEventFormState'
import { buildRepeatingEventConfig } from '../EventForm/recurringEventFormHelpers'
import { CadenceFormData } from '../EventForm/recurringScheduleModel'
import EventFormFields from '../EventForm/EventFormFields'
import { useSchedule } from '../ScheduleContext'

type Props = {
  number: number
  job: SchedulingJob
  defaultDate?: Dayjs
  defaultTimeMinutes?: number
  defaultDurationMinutes?: number
  initialEvent?: ScheduledEvent
  canRemove: boolean
  collapsed: boolean
  onCollapsedChange: (collapsed: boolean) => void
  onRemove: () => void
  onFormDataChange?: (data: CadenceFormData) => void
  open: boolean
  hideRepeatSelector?: boolean
}

export type CadenceSectionHandle = {
  validate: () => Promise<boolean>
}

const CadenceSection = forwardRef<CadenceSectionHandle, Props>(
  ({ number, job, defaultDate, defaultTimeMinutes, defaultDurationMinutes, initialEvent, canRemove, collapsed, onCollapsedChange, onRemove, onFormDataChange, open: modalOpen, hideRepeatSelector = false }, ref) => {

    const formState = useEventFormState({
      jobId: job.jobId,
      defaultDate,
      defaultDurationMinutes: defaultDurationMinutes ?? 15,
    })

    const { form, formValues, setFormValues } = formState
    const { crew } = useSchedule()

    const onFormDataChangeRef = useRef(onFormDataChange)
    onFormDataChangeRef.current = onFormDataChange

    useEffect(() => {
      if (onFormDataChangeRef.current) {
        onFormDataChangeRef.current({
          formValues,
          repeatingEventConfig: buildRepeatingEventConfig(formValues),
        })
      }
    }, [formValues])

    useEffect(() => {
      if (modalOpen && job) {
        const dateToUse = defaultDate || dayjs()

        if (initialEvent) {
          const eventStart = dayjs(initialEvent.start)
          const eventEnd = dayjs(initialEvent.end)
          const durationMinutes = eventEnd.diff(eventStart, 'minutes')
          const startTime = dayjs().startOf('day').hour(eventStart.hour()).minute(eventStart.minute())
          const estimatedDuration = Duration.minutes(durationMinutes)

          const crewMemberIdList = initialEvent.crewMemberIds
            ? initialEvent.crewMemberIds.split(',').map(id => parseInt(id)).filter(id => !isNaN(id))
            : []
          const selectedCrew = (crew || []).filter(c => crewMemberIdList.includes(c.id))

          const crewLead = initialEvent.crewForemanEmail
            ? (crew || []).find(c => c.userEmail === initialEvent.crewForemanEmail)
            : null

          const workareaIds = initialEvent.workAreaIds
            ? initialEvent.workAreaIds.split(',').map(id => parseInt(id)).filter(id => !isNaN(id))
            : []

          setFormValues({
            selectedCrew,
            crewLeadId: crewLead?.id || null,
            selectedWorkareaIds: workareaIds,
            isAllDay: initialEvent.allDay,
            endDateMode: initialEvent.allDay && dayjs(initialEvent.end).isSame(dateToUse, 'day') ? 'sameDay' : 'manual',
            startDate: dateToUse,
            endDate: initialEvent.allDay ? dayjs(initialEvent.end) : dateToUse,
            startTime,
            estimatedDuration,
            notes: initialEvent.notes || job.schedulingNotes || '',
            repeatUnit: 'none',
            repeatCount: 1,
            repeatPlace: ["1st"],
            repeatQuarter: [1],
            repeatMonth: [dateToUse.month() + 1],
            repeatEndType: 'date',
            repeatOccurrences: 1,
            repeatEndsOn: dateToUse,
          })

          form.setFieldsValue({
            startDate: dateToUse,
            startTime,
            estimatedDuration,
            crewMembers: crewMemberIdList,
            repeatUnit: 'none',
            repeatCount: 1,
            repeatPlace: ["1st"],
            repeatQuarter: [1],
            repeatMonth: [dateToUse.month() + 1],
          })
        } else {
          const startTime = defaultTimeMinutes !== undefined
            ? dayjs().startOf('day').add(defaultTimeMinutes, 'minutes')
            : dayjs().hour(8).minute(0)
          const estimatedDuration = Duration.minutes(defaultDurationMinutes ?? 15)

          setFormValues({
            selectedCrew: [],
            crewLeadId: null,
            selectedWorkareaIds: [],
            isAllDay: true,
            endDateMode: 'sameDay',
            startDate: dateToUse,
            endDate: dateToUse,
            startTime,
            estimatedDuration,
            notes: job.schedulingNotes || '',
            repeatUnit: 'none',
            repeatCount: 1,
            repeatPlace: ["1st"],
            repeatQuarter: [1],
            repeatMonth: [dateToUse.month() + 1],
            repeatEndType: 'date',
            repeatOccurrences: 1,
            repeatEndsOn: dateToUse,
          })

          form.setFieldsValue({
            startDate: dateToUse,
            startTime,
            estimatedDuration,
            crewMembers: [],
            repeatUnit: 'none',
            repeatCount: 1,
            repeatPlace: ["1st"],
            repeatQuarter: [1],
            repeatMonth: [dateToUse.month() + 1],
          })
        }
      }
    }, [modalOpen, job, form, defaultDate, defaultTimeMinutes, defaultDurationMinutes, initialEvent, crew])

    useImperativeHandle(ref, () => ({
      validate: async () => {
        try {
          await form.validateFields()
          return true
        } catch {
          onCollapsedChange(false)
          return false
        }
      },
    }))

    const { assignedWorkareas } = formState

    const headerDateText = formValues.startDate.format('MMM D, YYYY')
    const headerTimeText = formValues.isAllDay
      ? 'All Day'
      : formValues.startTime.format('h:mm A')
    const workareaSummary = assignedWorkareas.length > 0
      ? assignedWorkareas.map(w => w.name).join(', ')
      : null

    return (
      <div style={{
        background: '#f5f5f5',
        borderTop: '1px solid #f0f0f0',
        borderBottom: '1px solid #f0f0f0',
      }}>
        <div
          onClick={() => onCollapsedChange(!collapsed)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '16px 24px',
            cursor: 'pointer',
            background: '#fff',
            borderBottom: collapsed ? 'none' : '1px solid #f0f0f0',
            userSelect: 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, overflow: 'hidden', minWidth: 0 }}>
            {collapsed ? <RightOutlined style={{ fontSize: 12 }} /> : <DownOutlined style={{ fontSize: 12 }} />}
            <Badge
              count={number}
              style={{ backgroundColor: '#fa541c' }}
            />
            {workareaSummary && (
              <>
                <FontAwesomeIcon icon={faTrafficCone} style={{ color: '#999', fontSize: 13, flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                  {workareaSummary}
                </span>
              </>
            )}
            <CalendarOutlined style={{ color: '#999', fontSize: 14, flexShrink: 0 }} />
            <span style={{ fontSize: 14, color: '#333', flexShrink: 0, whiteSpace: 'nowrap' }}>{headerDateText}</span>
            <ClockCircleOutlined style={{ color: '#999', fontSize: 14, flexShrink: 0 }} />
            <span style={{ fontSize: 14, color: '#333', flexShrink: 0, whiteSpace: 'nowrap' }}>{headerTimeText}</span>
          </div>
          {canRemove && (
            <CloseOutlined
              style={{ fontSize: 12, color: '#999', flexShrink: 0 }}
              onClick={(e) => {
                e.stopPropagation()
                onRemove()
              }}
            />
          )}
        </div>
        <div style={{ padding: '0 24px 16px', display: collapsed ? 'none' : undefined }}>
          <Form form={form} layout="vertical">
            <EventFormFields
              formState={formState}
              showSelectAllButtons={true}
              hideRepeatSelector={hideRepeatSelector}
            />
          </Form>
        </div>
      </div>
    )
  }
)

export default CadenceSection
